import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ProjectsService } from '../../../../../core/projects.service';
import { AuthService } from '../../../../../../../shared/services';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { FetchContractorsListRequest } from '../../../../../core/projects.actions';
import { contractorsListInfo } from '../../../../../core/projects.selectors';
import { WarrantyPackageFormComponent } from './warranty-package-form.component';
import { environment } from 'src/environments/environment';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-warranty-package-detail',
  templateUrl: './warranty-package-detail.component.html',
  styleUrls: ['./warranty-package-detail.component.scss'],
  standalone: false,
})
export class WarrantyPackageDetailComponent implements OnInit, OnDestroy {
  @Input() data: any;
  pkg: any;
  projectId: string;
  role: string;
  userIsPrime = false;

  selectedTabIndex = 0;

  // Map
  claimLocations: { lat: number; lng: number }[] = [];
  mapCenter: { lat: number; lng: number } = { lat: 0, lng: 0 };

  // Action dropdown
  availableActions: { value: string; label: string }[] = [];
  actionDropdownOptions: { value: string; label: string }[] = [];
  actionForm: FormGroup;
  selectedAction = '';

  // Respond form
  respondForm: FormGroup;
  showRespondForm = false;
  respondClaimStatuses: { claim_id: number; claim_number: string; claim_title: string; status: string; comments: string }[] = [];

  // Forward form
  forwardForm: FormGroup;
  showForwardForm = false;
  forwardContractorOptions: any[] = [];
  forwardClaims: any[] = [];
  forwardAccounts: { account_id: number; name: string; email: string; selected: boolean }[] = [];

  statusMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    draft: { label: 'Draft', bgColor: 'bg-draft', textColor: 'txt-draft' },
    submitted: { label: 'Submitted', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    sent_to_contractor: { label: 'Sent to contractor', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    responded: { label: 'Responded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    verified: { label: 'Verified', bgColor: 'bg-approved', textColor: 'txt-approved' },
    closed: { label: 'Closed', bgColor: 'bg-approved', textColor: 'txt-approved' },
    sent_back: { label: 'Sent back', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
  };

  severityMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    low: { label: 'Low', bgColor: 'bg-sev-low', textColor: 'txt-sev-low' },
    medium: { label: 'Medium', bgColor: 'bg-sev-medium', textColor: 'txt-sev-medium' },
    high: { label: 'High', bgColor: 'bg-sev-high', textColor: 'txt-sev-high' },
    critical: { label: 'Critical', bgColor: 'bg-sev-critical', textColor: 'txt-sev-critical' },
  };

  packageStatusMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    draft: { label: 'Draft', bgColor: 'bg-pkg-draft', textColor: 'txt-pkg-draft' },
    sent: { label: 'Sent', bgColor: 'bg-pkg-sent', textColor: 'txt-pkg-sent' },
    responded: { label: 'Responded', bgColor: 'bg-pkg-responded', textColor: 'txt-pkg-responded' },
    closed: { label: 'Closed', bgColor: 'bg-pkg-closed', textColor: 'txt-pkg-closed' },
  };

  itemStatusOptions = [
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
  ];

  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private projectsService: ProjectsService,
    private authService: AuthService,
    private appHttpService: AppHttpService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
  ) {
    this.actionForm = new FormGroup({
      action: new FormControl(''),
    });
    this.respondForm = new FormGroup({
      description: new FormControl(''),
    });
    this.forwardForm = new FormGroup({
      title: new FormControl(''),
      description: new FormControl(''),
      sent_to_contractor_id: new FormControl(''),
      due_date: new FormControl(''),
    });
  }

  ngOnInit() {
    this.pkg = this.data;

    const url = this.router.url;
    const segments = url.split('?')[0].split('/');
    this.projectId = this.data?.projectId
      || this.route.snapshot.paramMap.get('projectId')
      || segments[segments.indexOf('projects') + 1]
      || '';

    const account = this.authService.getCurrentUser();
    this.role = account?.roles?.find(r => r.project_id === this.projectId)?.role
      || account?.role
      || '';

    // Fetch fresh detail
    if (this.pkg?.uuid || this.pkg?.id) {
      this.loadPackageDetail(this.pkg.uuid || this.pkg.id);
    }

    // Check if current user is a prime contractor
    if (this.isContractor()) {
      this.appHttpService.getService(`/v1/warranty/project/${this.projectId}/contractor-info`).subscribe({
        next: (resp: any) => {
          this.userIsPrime = resp?.data?.isPrime === true;
          this.buildAvailableActions();
        },
      });
    }

    // Fetch contractors for forward form
    this.store.dispatch(FetchContractorsListRequest({ payload: { projectId: this.projectId } }));
    this.store.select(contractorsListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.list) {
          this.forwardContractorOptions = data.list
            .filter(c => !c.prime)
            .map(c => ({
              value: c.id,
              label: c.name || c.poc || c.id,
            }));
          this.buildAvailableActions();
        }
      });
  }

  loadPackageDetail(packageId: number) {
    this.appHttpService.getService(`/v1/warranty/claim-packages/${packageId}`).subscribe({
      next: (resp: any) => {
        const detail = resp?.data || resp;
        this.pkg = { ...this.pkg, ...detail };
        this.buildAvailableActions();
        this.computeMapData();
      },
    });
  }

  computeMapData() {
    const claims = this.pkg?.claims || [];
    this.claimLocations = claims
      .filter(c => c.latitude && c.longitude)
      .map(c => ({ lat: Number(c.latitude), lng: Number(c.longitude) }));
    if (this.claimLocations.length > 0) {
      const sumLat = this.claimLocations.reduce((s, l) => s + l.lat, 0);
      const sumLng = this.claimLocations.reduce((s, l) => s + l.lng, 0);
      this.mapCenter = {
        lat: sumLat / this.claimLocations.length,
        lng: sumLng / this.claimLocations.length,
      };
    }
  }

  isPM(): boolean {
    return this.role === 'project-manager' || this.role === 'org-admin';
  }

  isContractor(): boolean {
    return this.role?.startsWith('contractor');
  }

  getInitials(firstName: string, lastName: string): string {
    return (firstName?.[0] || '') + (lastName?.[0] || '');
  }

  // --- Actions ---

  buildAvailableActions() {
    const actions = [];
    const status = this.pkg?.status;
    if (status === 'draft' && this.isPM()) {
      actions.push({ value: 'send', label: 'Send to Contractor' });
    }
    if (status === 'sent' && this.isContractor() && this.userIsPrime && this.forwardContractorOptions.length > 0) {
      actions.push({ value: 'forward', label: 'Forward to Subcontractor' });
    }
    if (status === 'responded' && this.isPM()) {
      actions.push({ value: 'close', label: 'Close Package' });
    }
    this.availableActions = actions;
    this.actionDropdownOptions = actions;

    // Subscribe to action form changes
    this.actionForm.get('action')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(val => {
        this.onActionChange(val);
      });
  }

  onActionChange(action: string) {
    this.selectedAction = action;
    if (action === 'respond') {
      this.openRespondForm();
    } else if (action === 'forward') {
      this.openForwardForm();
    }
  }

  confirmAction() {
    switch (this.selectedAction) {
      case 'send': this.sendPackage(); break;
      case 'respond': this.submitResponse(); break;
      case 'forward': this.submitForward(); break;
      case 'close': this.closePackage(); break;
    }
  }

  sendPackage() {
    if (!this.pkg?.id) return;
    this.appHttpService.postService(`/v1/warranty/claim-packages/${this.pkg.uuid}/send`, {}).subscribe({
      next: () => {
        this.projectsService.closeRightPanel.next();
      },
    });
  }

  closePackage() {
    if (!this.pkg?.id) return;
    const dialogData = new ConfirmDialogModel('Close Package', 'Close this package?', 'Close', 'Cancel');
    this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: dialogData })
      .afterClosed().subscribe(result => {
        if (!result) return;
        this.appHttpService.postService(`/v1/warranty/claim-packages/${this.pkg.uuid}/close`, {}).subscribe({
          next: () => {
            this.projectsService.closeRightPanel.next();
          },
        });
      });
  }

  editPackage() {
    this.projectsService.closeRightPanel.next();
    setTimeout(() => {
      this.projectsService.openRightPanel.next({
        component: WarrantyPackageFormComponent,
        data: {
          warranties: this.data?.warranties || [],
          projectId: this.projectId,
          editPackage: { ...this.pkg },
        },
      });
    }, 100);
  }

  deletePackage() {
    if (!this.pkg?.id) return;
    const dialogData = new ConfirmDialogModel('Delete Package', 'Delete this draft package? This cannot be undone.', 'Delete', 'Cancel');
    this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: dialogData })
      .afterClosed().subscribe(result => {
        if (!result) return;
        this.appHttpService.deleteService(`/v1/warranty/claim-packages/${this.pkg.uuid}`).subscribe({
          next: () => {
            this.projectsService.closeRightPanel.next();
          },
        });
      });
  }

  // --- Respond ---

  openRespondForm() {
    this.showRespondForm = true;
    this.showForwardForm = false;
    this.respondClaimStatuses = (this.pkg?.claims || []).map(claim => ({
      claim_id: claim.id,
      claim_number: claim.claim_number,
      claim_title: claim.title,
      status: '',
      comments: '',
    }));
  }

  submitResponse() {
    if (!this.pkg?.id) return;
    const payload = {
      description: this.respondForm.value.description || '',
      claim_statuses: this.respondClaimStatuses.map(cs => ({
        claim_id: cs.claim_id,
        status: cs.status || 'accepted',
        comments: cs.comments || '',
      })),
    };
    this.appHttpService.postService(`/v1/warranty/claim-packages/${this.pkg.uuid}/respond`, payload).subscribe({
      next: () => {
        this.showRespondForm = false;
        this.projectsService.closeRightPanel.next();
      },
    });
  }

  cancelRespond() {
    this.showRespondForm = false;
  }

  // --- Forward ---

  openForwardForm() {
    this.showForwardForm = true;
    this.showRespondForm = false;
    this.forwardAccounts = [];
    this.forwardClaims = (this.pkg?.claims || []).map(claim => ({
      claim_id: claim.id,
      claim_number: claim.claim_number,
      claim_title: claim.title,
      selected: true,
    }));
    this.forwardForm.patchValue({
      title: this.pkg?.title || '',
      description: '',
      due_date: this.pkg?.due_date || '',
    });

    // Watch contractor selection to load accounts
    this.forwardForm.get('sent_to_contractor_id')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(contractorId => {
        if (contractorId) {
          this.loadContractorAccounts(contractorId);
        } else {
          this.forwardAccounts = [];
        }
      });
  }

  loadContractorAccounts(contractorId: string) {
    this.appHttpService.getService(
      `/v1/warranty/project/${this.projectId}/contractors/${contractorId}/accounts`,
    ).subscribe({
      next: (resp: any) => {
        const accounts = resp?.data || [];
        this.forwardAccounts = accounts.map(a => ({
          account_id: a.account_id || a.id,
          name: `${a.first_name || ''} ${a.last_name || ''}`.trim(),
          email: a.email || '',
          selected: true,
        }));
      },
      error: () => { this.forwardAccounts = []; },
    });
  }

  toggleForwardAccount(account: any) {
    account.selected = !account.selected;
  }

  submitForward() {
    if (!this.pkg?.id) return;
    const val = this.forwardForm.value;
    const selectedClaims = this.forwardClaims.filter(c => c.selected).map(c => c.claim_id);
    if (!selectedClaims.length) return;

    const selectedAccountIds = this.forwardAccounts.filter(a => a.selected).map(a => a.account_id);
    const payload: any = {
      title: val.title || this.pkg?.title,
      description: val.description || '',
      sent_to_contractor_id: val.sent_to_contractor_id,
      claims: selectedClaims,
      due_date: val.due_date || undefined,
    };
    if (selectedAccountIds.length) {
      payload.notify_account_ids = selectedAccountIds;
    }
    this.appHttpService.postService(`/v1/warranty/claim-packages/${this.pkg.uuid}/forward`, payload).subscribe({
      next: () => {
        this.showForwardForm = false;
        this.projectsService.closeRightPanel.next();
      },
    });
  }

  cancelForward() {
    this.showForwardForm = false;
  }

  toggleForwardClaim(claim: any) {
    claim.selected = !claim.selected;
  }

  // --- KMZ Download ---

  downloadKmz() {
    if (!this.pkg?.id) return;
    this.appHttpService.getService(
      `/v1/warranty/claim-packages/${this.pkg.uuid}/kmz`,
      {},
      { responseType: 'blob' },
    ).subscribe({
      next: (resp: Blob) => {
        saveAs(resp, `${this.pkg.package_number || 'package'}.kmz`);
      },
    });
  }

  close() {
    this.projectsService.closeRightPanel.next();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
